/**
 * 盤を「開いた瞬間」 を判定するための小道具。
 *
 * 盤のアニメはどれも「いま何かが起きた」 ことを伝えるためのもの (石を置いた、
 * 駒が滑った、 直前の手にリングが出た) なので、 盤ごと mount した最初の描画で
 * 走らせると、 何も起きていないのに起きたように見える。 石の多い局面では
 * 全部が一斉に飛び出して、 読み込みに失敗したようにも見える。
 *
 * 使い方は 2 通り:
 *
 * ```tsx
 * // (1) key で再 mount する要素 (石など) — 生成時の値で固定する
 * const firstPaint = useFirstPaint();
 * <span className={`stone${firstPaint.current ? ' no-anim' : ''}`} />
 *
 * // (2) 同じ要素が残り続ける印 (last-move リングなど)
 * //     — mount 時の lastMove と同じ間だけ抑える
 * const freshLast = useFreshLastMove(lastMove);
 * <div className={`cell ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''}`} />
 * ```
 *
 * `no-anim` は styles.css で `animation: none` に落ちる (自身と ::before/::after、
 * 直下の .stone まで)。
 */

import { type RefObject, useEffect, useRef } from 'react';
import { contentKey } from './content-key';

/** mount 直後の描画だけ `current === true`。 */
export function useFirstPaint(): RefObject<boolean> {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = false;
  }, []);
  return ref;
}

/**
 * 手の同一性を見るための鍵。 **参照ではなく中身で比べる。**
 *
 * 参照で比べると、 他の盤で相手が指しただけで「新しい手が来た」 と誤判定し、
 * この盤の直前手がもう一度滑り込む。 理由は lib/content-key.ts に書いてある。
 */
export function moveKey(mv: unknown): string {
  return contentKey(mv);
}

/**
 * `lastMove` が mount 後に来た新しい手かどうか。
 * mount 時点の手と中身が同じなら false (= アニメを走らせない)。
 */
export function useFreshLastMove(lastMove: unknown): boolean {
  const key = moveKey(lastMove);
  const atMount = useRef(key);
  return key !== atMount.current;
}
